import axios from "axios";
import * as ApiInterfaces from "../interfaces/Apicall";
import * as env from "../env";
import { clearTwoFactorVerified } from "./twoFactorState";

const CODES_2FA: Record<string, string> = {
    two_factor_required: "/2fa/verification",
    two_factor_setup_required: "/2fa/configuration",
};

/**
 * Le drapeau "jeton déjà vérifié" (twoFactorState.ts) n'a pas de TTL côté
 * client — s'il devient faux côté serveur (reconfiguration du 2FA, jeton
 * expiré puis réémis…) avant que quelque chose ne pense à l'effacer, n'importe
 * quel appel admin échoue avec un message d'erreur générique sans issue
 * (ex: enregistrer un formulaire) plutôt que de renvoyer vers la
 * vérification. Centralisé ici — un seul endroit à corriger, peu importe
 * quel écran a déclenché l'appel.
 */
function redirigerSiDeuxFacteursRequis(code: string | undefined): void {
    if (!code || !CODES_2FA[code]) {
        return;
    }
    if (window.location.pathname.startsWith("/2fa/")) {
        return; // déjà sur l'écran concerné — éviter une boucle.
    }
    clearTwoFactorVerified();
    window.location.href = CODES_2FA[code];
}

class Apicall {
    return: ApiInterfaces.Response = {
        status: "OK",
        data: null,
    };

    constructor() {
        axios.defaults.headers.common["Accept"] = "application/json";
        axios.defaults.headers.common["Accept-Language"] = "fr";
    }

    async setAuthorizationHeader(): Promise<void> {
        const thetoken = localStorage.getItem("token");
        axios.defaults.headers.common["Authorization"] = `Bearer ${thetoken}`;

        return;
    }

    private resetReturn() {
        this.return = {
            status: "OK",
            data: null,
        };
    }

    async call(
        method: string,
        path: string,
        payload?: any,
    ): Promise<ApiInterfaces.Response> {
        await this.setAuthorizationHeader();
        this.resetReturn();
        if (method === "GET") {
            return axios
                .get(env.apiUrl + `/api/${path}`)
                .then((response) => {
                    this.return.status = response.data.status;
                    this.return.data = response.data.data;
                    if (this.return.status !== "OK") {
                        this.return.error = response.data.error;
                    }
                    return this.return;
                })
                .catch((error) => {
                    this.return.status = "ERROR";
                    this.return.error = "Un problème serveur est survenu";
                    if (error.response?.data?.error) {
                        this.return.error = error.response.data.error;
                    }
                    if (error.response?.data?.message) {
                        this.return.error = error.response.data.message;
                    }
                    if (error.response?.data?.code) {
                        this.return.code = error.response.data.code;
                        redirigerSiDeuxFacteursRequis(this.return.code);
                    }
                    return this.return;
                });
        }

        return axios
            .post(env.apiUrl + `/api/${path}`, payload)
            .then((response) => {
                this.return.status = response.data.status;
                this.return.data = response.data.data;
                if (this.return.status !== "OK") {
                    this.return.error = response.data.error;
                }
                return this.return;
            })
            .catch((error) => {
                this.return.status = "ERROR";
                this.return.error = "Un problème serveur est survenu";
                if (error.response?.data?.error) {
                    this.return.error = error.response.data.error;
                }
                if (error.response?.data?.message) {
                    this.return.error = error.response.data.message;
                }
                if (error.response?.data?.code) {
                    this.return.code = error.response.data.code;
                    redirigerSiDeuxFacteursRequis(this.return.code);
                }
                return this.return;
            });
    }

    /**
     * Télécharge un fichier binaire (PDF…) renvoyé par une route POST et
     * déclenche le téléchargement dans le navigateur — le nom de fichier
     * suggéré par le serveur (Content-Disposition) est utilisé s'il est
     * présent, sinon `nomParDefaut`.
     */
    async telechargerFichier(
        path: string,
        payload: any,
        nomParDefaut: string,
    ): Promise<ApiInterfaces.Response> {
        await this.setAuthorizationHeader();
        this.resetReturn();

        try {
            const response = await axios.post(env.apiUrl + `/api/${path}`, payload, {
                responseType: "blob",
            });
            const entete = response.headers["content-disposition"] as string | undefined;
            const correspondance = entete?.match(/filename="?([^"]+)"?/);
            const nom = correspondance?.[1] || nomParDefaut;

            const url = window.URL.createObjectURL(response.data);
            const lien = document.createElement("a");
            lien.href = url;
            lien.download = nom;
            document.body.appendChild(lien);
            lien.click();
            lien.remove();
            window.URL.revokeObjectURL(url);

            return this.return;
        } catch (error: any) {
            this.return.status = "ERROR";
            this.return.error = "Un problème serveur est survenu";
            // En responseType "blob", une erreur JSON arrive comme un Blob —
            // il faut le relire en texte pour en extraire le message.
            const donneesBlob = error.response?.data;
            if (donneesBlob instanceof Blob) {
                try {
                    const texte = await donneesBlob.text();
                    const donnees = JSON.parse(texte);
                    this.return.error = donnees.error || donnees.message || this.return.error;
                } catch {
                    // corps non-JSON : on garde le message générique.
                }
            }
            return this.return;
        }
    }

    async uploadFile(
        path: string,
        formData: FormData,
    ): Promise<ApiInterfaces.Response> {
        await this.setAuthorizationHeader();
        this.resetReturn();

        return axios
            .post(env.apiUrl + `/api/${path}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((response) => {
                this.return.status = response.data.status;
                this.return.data = response.data.data;
                if (this.return.status !== "OK") {
                    this.return.error = response.data.error;
                }
                return this.return;
            })
            .catch((error) => {
                this.return.status = "ERROR";
                this.return.error = "Un problème serveur est survenu";
                if (error.response?.data?.error) {
                    this.return.error = error.response.data.error;
                }
                if (error.response?.data?.message) {
                    this.return.error = error.response.data.message;
                }
                if (error.response?.data?.code) {
                    this.return.code = error.response.data.code;
                    redirigerSiDeuxFacteursRequis(this.return.code);
                }
                return this.return;
            });
    }
}

export default Apicall;
